/* Branding pentru overlay-ul de bootstrap /m/{slug} */

import { sanitizeImageSrc } from './url.js?v=82.86';

const BOOT_LOGO_CACHE_PREFIX = 'sj_boot_logo_';

export function formatSlugForDisplay(slug) {
  return String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function getSanitizedLocationLogoUrl(settings) {
  return sanitizeImageSrc(settings?.locationLogoURL || '');
}

function cacheBootstrapLogo(slug, logoUrl) {
  if (!slug || !logoUrl) return;
  try {
    sessionStorage.setItem(BOOT_LOGO_CACHE_PREFIX + slug, logoUrl);
  } catch (_) {}
}

/** Un singur rând de status — fără duplicat cu titlul. */
export function setPublicBootstrapLoadingHint(text) {
  const hintEl = document.getElementById('sjPublicBootstrapHint');
  const subtitleEl = document.getElementById('sjPublicBootstrapSubtitle');
  if (subtitleEl) subtitleEl.setAttribute('hidden', '');
  if (hintEl) hintEl.textContent = String(text || 'Se încarcă...').trim() || 'Se încarcă...';
}

export function setPublicBootstrapTitle(text) {
  const titleEl = document.getElementById('sjPublicBootstrapTitle');
  const subtitleEl = document.getElementById('sjPublicBootstrapSubtitle');
  if (!titleEl) return;
  const t = String(text || '').trim();
  if (t) {
    titleEl.textContent = t;
    titleEl.removeAttribute('hidden');
  } else {
    titleEl.textContent = '';
    titleEl.setAttribute('hidden', '');
  }
  if (subtitleEl) subtitleEl.setAttribute('hidden', '');
}

export function initEarlyPublicBootstrapFromPath() {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname || '';
  const m = path.match(/^\/m\/([a-z0-9-]{3,48})\/?$/i);
  if (!m) return null;
  const slug = m[1].toLowerCase();
  document.documentElement.classList.add('sj-public-bootstrap-active');
  setPublicBootstrapTitle('');
  setPublicBootstrapLoadingHint('Se încarcă...');
  tryShowCachedBootstrapLogo(slug);
  return slug;
}

function tryShowCachedBootstrapLogo(slug) {
  const wrap = document.getElementById('sjPublicBootstrapLogoWrap');
  const img = document.getElementById('sjPublicBootstrapLogo');
  const root = document.getElementById('sjPublicBootstrap');
  if (!slug || !wrap || !img) return false;
  try {
    const cached = sessionStorage.getItem(BOOT_LOGO_CACHE_PREFIX + slug);
    if (!cached) return false;
    wrap.removeAttribute('hidden');
    img.src = cached;
    if (root) root.classList.add('sj-public-bootstrap--has-logo');
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Logo mare pe overlay; titlul rămâne numele locației (nu se dublează „Se încarcă”).
 * @returns {Promise<boolean>}
 */
export function applyPublicBootstrapLogo(settings, options = {}) {
  const minVisibleMs = Math.max(0, Number(options.minVisibleMs) || 0);
  const slug = options.slug || null;
  const root = document.getElementById('sjPublicBootstrap');
  const wrap = document.getElementById('sjPublicBootstrapLogoWrap');
  const img = document.getElementById('sjPublicBootstrapLogo');
  const logoUrl = getSanitizedLocationLogoUrl(settings);
  const company = String(settings?.companyName || '').trim();

  setPublicBootstrapTitle(company);
  setPublicBootstrapLoadingHint('Se deschide…');

  if (!root) return Promise.resolve(false);

  if (!logoUrl || !wrap || !img) {
    wrap?.setAttribute('hidden', '');
    img?.removeAttribute('src');
    root.classList.remove('sj-public-bootstrap--has-logo');
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;
    const done = (ok) => {
      if (settled) return;
      settled = true;
      if (ok && slug) cacheBootstrapLogo(slug, logoUrl);
      if (ok && minVisibleMs > 0) {
        window.setTimeout(() => resolve(true), minVisibleMs);
      } else {
        resolve(!!ok);
      }
    };

    wrap.removeAttribute('hidden');
    root.classList.add('sj-public-bootstrap--has-logo');

    const failTimer = window.setTimeout(() => done(false), 8000);

    img.onload = () => {
      window.clearTimeout(failTimer);
      applyBrandBackgroundFromLogo(logoUrl, slug);
      done(true);
    };
    img.onerror = () => {
      window.clearTimeout(failTimer);
      wrap.setAttribute('hidden', '');
      root.classList.remove('sj-public-bootstrap--has-logo');
      done(false);
    };

    if (img.src === logoUrl && img.complete && img.naturalWidth > 0) {
      window.clearTimeout(failTimer);
      applyBrandBackgroundFromLogo(logoUrl, slug);
      done(true);
      return;
    }

    img.alt = company ? `Logo ${company}` : 'Logo locație';
    img.src = logoUrl;
  });
}

/** Nu afișăm logo pe interfața client live — doar pe overlay-ul de bootstrap. */
export function applyClientPublicBrandLogo(_settings) {
  const el = document.getElementById('clientPublicBrandLogo');
  if (!el) return;
  el.style.display = 'none';
  el.setAttribute('hidden', '');
  el.removeAttribute('src');
}

/**
 * Fundalul ecranului de încărcare = culoarea marginii logo-ului (dacă marginea e uniformă și opacă).
 * Logo pe fundal transparent / alb / variat → rămâne fundalul închis implicit.
 */
function applyBrandBackgroundFromLogo(logoUrl, slug) {
  const root = document.getElementById('sjPublicBootstrap');
  if (!root || !logoUrl) return;
  const probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.onload = () => {
    try {
      const n = 40;
      const c = document.createElement('canvas');
      c.width = n; c.height = n;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(probe, 0, 0, n, n);
      const d = ctx.getImageData(0, 0, n, n).data;
      const px = [];
      for (let i = 0; i < n; i++) {
        for (const [x, y] of [[i, 0], [i, n - 1], [0, i], [n - 1, i]]) {
          const k = (y * n + x) * 4;
          px.push([d[k], d[k + 1], d[k + 2], d[k + 3]]);
        }
      }
      if (px.filter((p) => p[3] > 230).length < px.length * 0.9) return clearBrand(root, slug);
      // culoarea dominantă a marginii (un inel colorat sau un text care atinge marginea nu o strică)
      const buckets = new Map();
      px.forEach((p) => {
        const k = `${p[0] >> 5}-${p[1] >> 5}-${p[2] >> 5}`;
        const b = buckets.get(k) || [];
        b.push(p);
        buckets.set(k, b);
      });
      const top = [...buckets.values()].sort((a, b) => b.length - a.length)[0] || [];
      if (!top.length) return clearBrand(root, slug);
      const seed = [0, 1, 2].map((j) => top.reduce((a, p) => a + p[j], 0) / top.length);
      const near = px.filter((p) => Math.hypot(p[0] - seed[0], p[1] - seed[1], p[2] - seed[2]) < 48);
      if (near.length < px.length * 0.5) return clearBrand(root, slug);
      const avg = [0, 1, 2].map((j) => near.reduce((a, p) => a + p[j], 0) / near.length);
      const lum = 0.299 * avg[0] + 0.587 * avg[1] + 0.114 * avg[2];
      if (lum > 242) return clearBrand(root, slug);
      const hex = `#${avg.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
      root.style.background = hex;
      root.classList.add('sj-boot--brand');
      root.classList.toggle('sj-boot--light', lum > 165);
      if (slug) { try { localStorage.setItem(`sj_boot_bg_${slug}`, hex); } catch (_) { /* ignore */ } }
    } catch (_) { /* CORS / canvas indisponibil → rămâne fundalul implicit */ }
  };
  probe.src = logoUrl;
}

function clearBrand(root, slug) {
  root.style.background = '';
  root.classList.remove('sj-boot--brand', 'sj-boot--light');
  if (slug) { try { localStorage.removeItem(`sj_boot_bg_${slug}`); } catch (_) { /* ignore */ } }
}
